import { useState, useEffect } from 'react';
import PaymentSuccess from './PaymentSuccess';
import BillingDashboard from './BillingDashboard';
import { AlertCircle } from 'lucide-react';

const envApiUrl = process.env.REACT_APP_API_BASE_URL || '';
const SERVER_URL = envApiUrl ? envApiUrl.replace('/api', '') : '';

function getRenewDate(daysFromNow = 30) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().split('T')[0];
}

const PLAN_PRESETS = {
  free_trial:   { maxAiAgents: 1, maxHumanAgents: 1, maxUsers: 1, creditsTotal: 10000, price: 0 },
  starter:      { maxAiAgents: 3, maxHumanAgents: 3, maxUsers: 3, creditsTotal: 5000,  price: 999 },
  growth:       { maxAiAgents: 5, maxHumanAgents: 5, maxUsers: 5, creditsTotal: 25000, price: 2499 },
  professional: { maxAiAgents: 7, maxHumanAgents: 7, maxUsers: 7, creditsTotal: 75000, price: 4999 },
  enterprise:   { maxAiAgents: 999, maxHumanAgents: 999, maxUsers: 999, creditsTotal: 999999, price: 9999 },
};

function buildSubscription(plan, amount, existing, extraAi = 0, extraHuman = 0) {
  const key    = typeof plan === 'string' ? plan.toLowerCase() : (plan.id ? plan.id.toLowerCase() : '');
  const preset = PLAN_PRESETS[key] || { maxAiAgents: 0, maxHumanAgents: 0, maxUsers: 0, creditsTotal: 0 };
  const planName = typeof plan === 'string' ? plan : plan.name;
  const isAnnual =
    planName?.toLowerCase().includes('annual') ||
    planName?.toLowerCase().includes('yearly');
  const renewsAt = getRenewDate(isAnnual ? 365 : 30);
  return {
    planName,
    planId:        key,
    planDbId:      typeof plan === 'string' ? plan : plan.id,
    planPrice:     amount,
    renewsAt,
    maxAiAgents:   preset.maxAiAgents + extraAi,
    maxHumanAgents: preset.maxHumanAgents + extraHuman,
    maxUsers:      preset.maxUsers + extraHuman,
    maxAgents:     preset.maxAiAgents + extraAi,
    creditsUsed:   existing?.creditsUsed  ?? 0,
    creditsTotal:  existing?.creditsTotal ?? preset.creditsTotal,
    topupCredits:  existing?.topupCredits ?? 0,
    nextBilling:   amount,
    activatedAt:   new Date().toISOString(),
    service_active: true,
  };
}

async function loadRazorpaySDK() {
  return new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const s = document.createElement('script');
    s.src     = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload  = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

async function launchRazorpay({ amount, planName, userName, tenantId, planId, SERVER_URL, onSuccess, onFail, onDismiss, extraAiAgents = 0, extraHumanAgents = 0, extraCredits = null }) {
  const loaded = await loadRazorpaySDK();
  if (!loaded || !window.Razorpay) { onFail('Razorpay SDK failed to load'); return; }

  let orderId, keyId;
  try {
    const res = await fetch(`${SERVER_URL}/api/payment/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount, currency: 'INR', planId, planName, userName, tenantId }),
    });
    const d = await res.json();
    if (!res.ok) throw new Error(d.error || 'Order creation failed');
    orderId = d.orderId; keyId = d.keyId;
  } catch (e) { onFail(e.message); return; }

  const rzp = new window.Razorpay({
    key: keyId, amount: amount * 100, currency: 'INR',
    name: 'EasyChat', description: planName, order_id: orderId,
    prefill: { name: userName, email: 'customer@example.com' },
    theme: { color: '#82bbe0' },
    handler: async (response) => {
      try {
        const vres = await fetch(`${SERVER_URL}/api/payment/verify`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            planName, amount, userName, tenantId,
            planId,
            extraAiAgents,
            extraHumanAgents,
            extraCredits,
          }),
        });
        const vd = await vres.json();
        if (vd.success) onSuccess({ paymentId: response.razorpay_payment_id });
        else onFail('Payment verification failed. Contact support.');
      } catch { onFail('Verification error. Contact support.'); }
    },
    modal: { ondismiss: onDismiss },
  });
  rzp.on('payment.failed', (r) => onFail(r.error?.description || 'Payment failed'));
  rzp.open();
}

// ── Payment history helpers ────────────────────────────────────────────────────
function loadPaymentHistory() {
  try { return JSON.parse(localStorage.getItem('easychat_payment_history') || '[]'); }
  catch { return []; }
}

function savePaymentHistory(records) {
  localStorage.setItem('easychat_payment_history', JSON.stringify(records));
}

function addPaymentRecord({ paymentId, planName, planType, amount, renewsAt }) {
  const history = loadPaymentHistory();
  const record = {
    paymentId,
    planName,
    planType,
    amount,
    paidAt: new Date().toISOString(),
    validUntil: renewsAt || null,
  };
  history.unshift(record);
  savePaymentHistory(history);
  return history;
}

export default function PaymentPage({ userName, tenantId }) {
  const [loadingId, setLoadingId]           = useState('');
  const [error, setError]                   = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [subscription, setSubscription]     = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);

  // ── Load subscription from DB on mount (so SQL updates reflect immediately) ──
  useEffect(() => {
    setPaymentHistory(loadPaymentHistory());

    async function loadSubscription() {
      try {
        const res  = await fetch(`${SERVER_URL}/api/subscription/${tenantId}`);
        const json = await res.json();

        if (json.success && json.data) {
          const sub = json.data;

          const key    = sub.plan_id ? sub.plan_id.toLowerCase() : '';
          const preset = PLAN_PRESETS[key] || { maxAiAgents: 0, maxHumanAgents: 0, maxUsers: 0, creditsTotal: 0 };
          const mapped = {
            planName:       sub.plan_name,
            planId:         sub.plan_id,
            planPrice:      parseFloat(sub.amount) || 0,
            renewsAt:       sub.next_billing,
            maxAiAgents:    preset.maxAiAgents + (sub.extra_ai_agents || 0),
            maxHumanAgents: preset.maxHumanAgents + (sub.extra_human_agents || 0),
            maxUsers:       preset.maxUsers + (sub.extra_human_agents || 0),
            maxAgents:      preset.maxAiAgents + (sub.extra_ai_agents || 0),
            creditsUsed:    0,
            creditsTotal:   preset.creditsTotal,
            topupCredits:   0,
            service_active: sub.status === 'ACTIVE',
          };

          // Pull credits from DB too
          try {
            const cres  = await fetch(`${SERVER_URL}/api/credits/${tenantId || userName}`);
            const cjson = await cres.json();
            if (cjson.success && cjson.data) {
              mapped.creditsUsed  = cjson.data.credits_used    ?? 0;
              mapped.creditsTotal = cjson.data.monthly_credits ?? 0;
              mapped.topupCredits = cjson.data.topup_credits   ?? 0;
            }
          } catch {}

          localStorage.setItem('easychat_subscription', JSON.stringify(mapped));
          setSubscription(mapped);
        } else {
          // No DB record — default to No Plan
          const stored = localStorage.getItem('easychat_subscription');
          if (stored) {
            try { setSubscription(JSON.parse(stored)); } catch {}
          } else {
            const defaultSub = {
              planName:       'No Plan',
              planId:         null,
              planPrice:      0,
              renewsAt:       null,
              maxAiAgents:    0,
              maxHumanAgents: 0,
              maxUsers:       0,
              maxAgents:      0,
              creditsUsed:    0,
              creditsTotal:   0,
              topupCredits:   0,
              service_active: false,
            };
            setSubscription(defaultSub);
          }
        }
      } catch {
        // Network error — fall back to localStorage, then No Plan
        const stored = localStorage.getItem('easychat_subscription');
        if (stored) {
          try { setSubscription(JSON.parse(stored)); } catch {}
        } else {
          setSubscription({
            planName: 'No Plan', planId: null, planPrice: 0,
            renewsAt: null,
            maxAiAgents: 0, maxHumanAgents: 0,
            maxUsers: 0, maxAgents: 0,
            creditsUsed: 0, creditsTotal: 0,
            topupCredits: 0, service_active: false,
          });
        }
      }
    }

    loadSubscription();
  }, [userName, tenantId]);

  const handleSubscriptionChange = (updated) => {
    setSubscription(updated);
    localStorage.setItem('easychat_subscription', JSON.stringify(updated));
  };

  // ── Subscription click → Razorpay ─────────────────────────────────────────
  const handleSubscriptionPay = async (planKey, planLabel, basePrice, billingCycle = 'monthly', addOns = null) => {
    if (loadingId) return;
    const lid = 'plan_' + planKey;
    setLoadingId(lid); setError('');

    const isYearly   = billingCycle === 'yearly';
    const finalPrice = isYearly ? Math.round(basePrice * 0.8) * 12 : basePrice;
    const gst        = Math.round(finalPrice * 0.18);
    const total      = addOns?.total ? addOns.total : (finalPrice + gst);
    const planLabelWithCycle = isYearly ? `${planLabel} (Yearly)` : planLabel;

    const extraAiAgents = addOns?.extraAiAgents || 0;
    const extraHumanAgents = addOns?.extraHumanAgents || 0;
    const extraCredits = addOns?.extraCredits || null;

    await launchRazorpay({
      amount: total, planName: planLabelWithCycle, userName, tenantId,
      planId: planKey, SERVER_URL,
      extraAiAgents, extraHumanAgents, extraCredits,
      onSuccess: async ({ paymentId }) => {
        const newSub = buildSubscription(planKey, planLabelWithCycle, total, subscription, extraAiAgents, extraHumanAgents);
        if (isYearly) newSub.renewsAt = getRenewDate(365);
        localStorage.setItem('easychat_subscription', JSON.stringify(newSub));
        setSubscription(newSub);
        const newHistory = addPaymentRecord({
          paymentId,
          planName: planLabelWithCycle,
          planType: 'subscription',
          amount: total,
          renewsAt: newSub.renewsAt,
        });
        setPaymentHistory(newHistory);
        setPaymentSuccess({ paymentId, plan: { name: planLabelWithCycle, category: 'subscription' }, amount: total });
        setLoadingId('');
      },
      onFail:    (msg) => { setError(msg); setLoadingId(''); },
      onDismiss: () => setLoadingId(''),
    });
  };

  // ── Credit top-up click → Razorpay ────────────────────────────────────────
  const handleCreditPay = async (pack) => {
    if (loadingId) return;
    const lid = 'topup_' + pack.k;
    setLoadingId(lid); setError('');
    const gst      = Math.round(pack.price * 0.18);
    const total    = pack.price + gst;
    const topupPlanId = 'topup_' + pack.k;
    const topupPlanName = pack.k + ' Credit Top-Up';

    await launchRazorpay({
      amount: total, planName: topupPlanName, userName, tenantId,
      planId: topupPlanId, SERVER_URL,
      onSuccess: ({ paymentId }) => {
        const creditMap = { '25K': 25000, '100K': 100000, '500K': 500000 };
        const added     = creditMap[pack.k] || 0;
        const newSub    = {
          ...(subscription || buildSubscription(topupPlanId, topupPlanName, total, null)),
          topupCredits: (subscription?.topupCredits || 0) + added,
        };
        localStorage.setItem('easychat_subscription', JSON.stringify(newSub));
        setSubscription(newSub);
        const newHistory = addPaymentRecord({
          paymentId,
          planName: topupPlanName,
          planType: 'topup',
          amount: total,
          renewsAt: null,
        });
        setPaymentHistory(newHistory);
        setPaymentSuccess({ paymentId, plan: { name: topupPlanName, category: 'topup' }, amount: total });
        setLoadingId('');
      },
      onFail:    (msg) => { setError(msg); setLoadingId(''); },
      onDismiss: () => setLoadingId(''),
    });
  };

  if (paymentSuccess) {
    return (
      <PaymentSuccess
        paymentId={paymentSuccess.paymentId}
        plan={paymentSuccess.plan}
        amount={paymentSuccess.amount}
        userName={userName}
        onDone={() => setPaymentSuccess(null)}
      />
    );
  }

  return (
    <div className="bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn flex justify-center items-start border-t border-gray-100 w-full">
      <div className="w-full max-w-6xl space-y-12">
        {error && (
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm max-w-4xl mx-auto shadow-sm">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span className="font-medium">{error}</span>
            <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">✕</button>
          </div>
        )}

        <BillingDashboard
          userName={userName}
          subscription={subscription}
          onSubscriptionChange={handleSubscriptionChange}
          onSubscriptionPay={handleSubscriptionPay}
          onCreditPay={handleCreditPay}
          loadingId={loadingId}
          paymentHistory={paymentHistory}
          serverUrl={SERVER_URL}
        />
      </div>
    </div>
  );
}